/**
 * My lists page: compact table of the user's lists.
 */
import React, { useMemo, useState } from "react";
import { Plus, Upload } from "lucide-react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type SortingState,
} from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, DataTablePagination } from "@/components/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { SearchField } from "@/components/shared/search-field";

import { useLists } from "./hooks/use-lists";
import { CreateListModal } from "./components/create-list-modal";
import { ImportTakeoutModal } from "./components/import-takeout-modal";
import { listColumns } from "./components/list-columns";

const PAGE_SIZE = 25;

const ListsPage: React.FC = () => {
  const { data: listsResponse, isLoading, error } = useLists();
  const [sorting, setSorting] = useState<SortingState>([{ id: "updatedAt", desc: true }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [isImportOpen, setIsImportOpen] = useState(false);

  const lists = useMemo(() => listsResponse?.data || [], [listsResponse?.data]);

  const table = useReactTable({
    data: lists,
    columns: listColumns,
    state: { sorting, globalFilter, columnFilters },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    initialState: { pagination: { pageSize: PAGE_SIZE } },
    getRowId: (r) => r.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: "includesString",
  });

  const handleTypeFilter = (value: string) => {
    setTypeFilter(value);
    setColumnFilters(value === "all" ? [] : [{ id: "listType", value }]);
  };

  const totalPlaces = lists.reduce((sum, l) => sum + (l.itemCount || 0), 0);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="My"
        emphasis="lists"
        stats={[
          { value: lists.length, label: lists.length === 1 ? "list" : "lists" },
          { value: totalPlaces, label: "places" },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchField id="list-search" value={globalFilter} onChange={setGlobalFilter} placeholder="Search lists" />
        <Select value={typeFilter} onValueChange={handleTypeFilter}>
          <SelectTrigger className="h-8 w-[140px] text-[13px]" aria-label="List type">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="featured">Featured</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={() => setIsImportOpen(true)}>
            <Upload aria-hidden /> Import
          </Button>
          <CreateListModal
            trigger={
              <Button variant="brand">
                <Plus aria-hidden /> Create list
              </Button>
            }
          />
        </div>
      </div>

      {error ? (
        <p className="text-[13.5px] text-muted-foreground">Could not load your lists. Reload the page to try again.</p>
      ) : (
        <DataTable
          table={table}
          isLoading={isLoading}
          emptyMessage={lists.length === 0 ? "No lists yet." : "No lists match your search."}
          emptyAction={
            lists.length === 0 ? (
              <CreateListModal trigger={<button type="button" className="text-brand underline underline-offset-[3px]">Create your first list</button>} />
            ) : undefined
          }
          footer={<DataTablePagination table={table} noun="lists" />}
        />
      )}

      <ImportTakeoutModal isOpen={isImportOpen} onOpenChange={setIsImportOpen} />
    </div>
  );
};

export default ListsPage;
